import { Box } from '@mantine/core';
import { getReferenceString } from '@medplum/core';
import { CSSProperties, ReactElement, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMarketOptions } from '@/hooks/useMarketOptions';
import { useNow } from '@/hooks/useNow';
import { alert } from '@/services/alert';
import { usePacQueueHydration } from '../../hooks/usePacQueueHydration';
import { usePatientListFilters } from '../../hooks/usePatientListFilters';
import { useHubFilterValues } from '../../hooks/useHubFilterValues';
import { QUEUE_PAGE_ROWS } from '../../hooks/useQueuePage';
import { useWonEvents } from '../../hooks/useWonEvents';
import { pnConsultationFor } from '../../services/listRowShared';
import {
  COVERAGE_TRACK_FILTER_OPTIONS,
  COVERAGE_TRACKS_FILTER,
  isQueueFiltered,
  matchesPatientFilterTags,
  optionsFromRows,
  QUEUE_FILTER_ANY,
  QUEUE_FILTERS_OFF,
  QueueFilterOption,
  QueueFilterValues,
  TRACK_FILTER_OPTIONS,
} from '../../services/queueFilters';
import {
  buildWonRows,
  latestWins,
  matchesProcedureFilter,
  WonLeadRow,
  WonProcedureFilter,
} from '../../services/wonRows';
import { summarizeWins, summarizeWinsByTrack } from '../../services/wonSummary';
import { CohortTruncationNotice } from '../../components/CohortTruncationNotice';
import { ListRangeControl } from '@/components/Hub/ListRangeControl';
import { QueueFilterBar } from '@/components/Hub/QueueFilterBar';
import { QueueFooter } from '@/components/Hub/QueueFooter';
import { QueueTable } from '../../components/QueueTable';
import { patientColumn, paymentTrackColumn, pnColumn, pnConsultationColumn } from './hubColumns';
import { notesColumn } from './notesColumn';
import { useHubRowEdits } from './useHubRowEdits';
import { closedColumn, procedureScheduledColumn } from './wonColumns';
import { CoverageWonSummary, WonSummary } from './WonSummary';

/**
 * The Intake hub's Won tab (BAR-2805): who was won in the period. The whole range
 * comes from the Won ledger (BAR-2804) and is filtered and paged in the browser,
 * so N is the true count.
 */

const DEFAULT_PRESET = 'this-month';

const WON_PRESETS = [
  { id: 'this-month' as const, label: 'This month' },
  { id: 'last-month' as const, label: 'Last month' },
  { id: 'this-quarter' as const, label: 'This quarter' },
  { id: 'year-to-date' as const, label: 'Year to date' },
  { id: 'custom' as const, label: 'Custom range' },
];

const PROCEDURE_FILTER_OPTIONS: QueueFilterOption[] = [
  { value: 'all', label: 'All' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'not-scheduled', label: 'Not scheduled' },
];

const PATIENT_COLUMN_SIZE = 190;

const HYDRATION_NEEDS = {
  procedure: true,
  callNotes: true,
  careTeam: true,
  eligibility: true,
  stickyNotes: true,
} as const;

/** Plus the consults, for the PN consultation column. */
const COVERAGE_HYDRATION_NEEDS = { ...HYDRATION_NEEDS, consult: true } as const;

/** Employer and insurance by default; self-pay on request (BAR-3099). */
const COVERAGE_DEFAULTS: QueueFilterValues = { ...QUEUE_FILTERS_OFF, track: COVERAGE_TRACKS_FILTER };

const TAB_STYLE: CSSProperties = { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' };

export interface WonTabProps {
  /** Injectable for tests; defaults to a clock that keeps ticking. */
  now?: Date;
  /**
   * `coverage`, the Coverage pill's Won (BAR-3099): no PN filter, the coverage tracks by default,
   * a PN consultation column and the board by track.
   */
  variant?: 'intake' | 'coverage';
}

export function WonTab({ now, variant = 'intake' }: WonTabProps): ReactElement {
  const navigate = useNavigate();
  const resolvedNow = useNow(now);
  const coverage = variant === 'coverage';

  const filters = usePatientListFilters<WonLeadRow>({
    resetKey: coverage ? 'coverage-won' : 'intake-won',
    chipDefinitions: [],
    defaultPreset: DEFAULT_PRESET,
    now: resolvedNow,
  });

  const hubFilters = useHubFilterValues(coverage ? COVERAGE_DEFAULTS : QUEUE_FILTERS_OFF);
  const { setValues, setFilter, pnOptions } = hubFilters;
  // No PN filter on Coverage, so the PN default never applies there.
  const values = useMemo(
    () => (coverage ? { ...hubFilters.values, pn: QUEUE_FILTER_ANY } : hubFilters.values),
    [coverage, hubFilters.values]
  );
  const defaults = useMemo(
    () => (coverage ? { ...hubFilters.defaults, pn: QUEUE_FILTER_ANY } : hubFilters.defaults),
    [coverage, hubFilters.defaults]
  );
  const [procedureFilter, setProcedureFilter] = useState<WonProcedureFilter>('all');

  const won = useWonEvents(filters.range);

  useEffect(() => {
    if (won.error) {
      alert.error(`Couldn't load Won: ${won.error}`);
    }
  }, [won.error]);

  const wins = useMemo(() => latestWins(won.events), [won.events]);
  const patientRefs = useMemo(
    () => wins.filter((win) => win.medplumPatientId).map((win) => `Patient/${win.medplumPatientId}`),
    [wins]
  );
  const hydration = usePacQueueHydration(
    patientRefs,
    coverage ? COVERAGE_HYDRATION_NEEDS : HYDRATION_NEEDS,
    `${variant}-won:${filters.range.from}|${filters.range.to}`
  );
  const builtRows = useMemo(
    () =>
      buildWonRows(wins, won.patientById, hydration).map((row) =>
        coverage
          ? { ...row, pnConsultation: pnConsultationFor(hydration, getReferenceString(row.patient) ?? '', resolvedNow) }
          : row
      ),
    [wins, won.patientById, hydration, coverage, resolvedNow]
  );

  const { rows, onNoteSaved } = useHubRowEdits(builtRows, QUEUE_FILTER_ANY);

  const visibleRows = useMemo(
    () =>
      rows.filter(
        (row) => matchesPatientFilterTags(row.patient, values) && matchesProcedureFilter(row, procedureFilter)
      ),
    [rows, values, procedureFilter]
  );

  const [page, setPage] = useState(1);
  const pageKey = JSON.stringify([filters.range, values, procedureFilter]);
  const [prevPageKey, setPrevPageKey] = useState(pageKey);
  // Any filter change returns to page one.
  if (prevPageKey !== pageKey) {
    setPrevPageKey(pageKey);
    setPage(1);
  }
  const pageRows = useMemo(
    () => visibleRows.slice((page - 1) * QUEUE_PAGE_ROWS, page * QUEUE_PAGE_ROWS),
    [visibleRows, page]
  );

  const { markets } = useMarketOptions();
  const locationOptions = useMemo(() => optionsFromRows(markets, 'All'), [markets]);

  // The whole range, not the filtered rows: the PN filter tints its row instead.
  const summary = useMemo(
    () => summarizeWins(builtRows, new Map(pnOptions.map((option) => [option.value, option.label])), values.pn),
    [builtRows, pnOptions, values.pn]
  );
  const coverageSummary = useMemo(() => summarizeWinsByTrack(builtRows), [builtRows]);

  const columns = useMemo(
    () => [
      { ...patientColumn<WonLeadRow>(), size: PATIENT_COLUMN_SIZE },
      paymentTrackColumn<WonLeadRow>(),
      coverage ? pnConsultationColumn<WonLeadRow>() : pnColumn<WonLeadRow>(),
      closedColumn<WonLeadRow>(),
      procedureScheduledColumn<WonLeadRow>(),
      notesColumn<WonLeadRow>({ onSaved: onNoteSaved }),
    ],
    [onNoteSaved, coverage]
  );

  return (
    <Box style={TAB_STYLE}>
      <QueueFilterBar
        date={
          <ListRangeControl
            label="Won"
            presets={WON_PRESETS}
            presetId={filters.presetId}
            value={filters.range}
            onChange={filters.setRange}
          />
        }
        filters={[
          ...(coverage ? [] : [{ label: 'PN', value: values.pn, options: pnOptions, onChange: setFilter('pn') }]),
          {
            label: 'Payment track',
            value: values.track,
            options: coverage ? COVERAGE_TRACK_FILTER_OPTIONS : TRACK_FILTER_OPTIONS,
            onChange: setFilter('track'),
          },
          { label: 'Location', value: values.location, options: locationOptions, onChange: setFilter('location') },
          {
            label: 'Procedure',
            value: procedureFilter,
            options: PROCEDURE_FILTER_OPTIONS,
            onChange: (value) => setProcedureFilter(value as WonProcedureFilter),
          },
        ]}
        isFiltered={
          isQueueFiltered(values, defaults) || procedureFilter !== 'all' || filters.presetId !== DEFAULT_PRESET
        }
        onReset={() => {
          setValues(defaults);
          setProcedureFilter('all');
          filters.setRange(DEFAULT_PRESET);
        }}
      />
      {won.hasMore && <CohortTruncationNotice loaded={wins.length} />}
      {coverage ? (
        <CoverageWonSummary summary={coverageSummary} />
      ) : (
        <WonSummary summary={summary} selectedPn={values.pn} />
      )}
      <QueueTable
        columns={columns}
        data={pageRows}
        loading={won.loading || hydration.loading}
        initialSorting={[{ id: 'closed', desc: true }]}
        onOpenPatient={(patientId) => navigate(`/Patient/${patientId}/timeline`)}
        emptyText="No patients match these filters"
      />
      <QueueFooter
        shown={pageRows.length}
        total={visibleRows.length}
        page={page}
        pageCount={Math.ceil(visibleRows.length / QUEUE_PAGE_ROWS)}
        onPageChange={setPage}
      />
    </Box>
  );
}
